import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

const FEATURES = [
  {
    icon: "ri-focus-3-line",
    title: "정밀 유량 제어",
    desc: "전기 신호에 따라 유압 유량을 정밀하게 제어하여 빠르고 정확한 응답을 구현합니다.",
  },
  {
    icon: "ri-arrow-left-right-line",
    title: "정밀 방향 제어",
    desc: "유압 액추에이터와 실린더의 움직임을 정밀하게 제어할 수 있습니다.",
  },
  {
    icon: "ri-speed-line",
    title: "고응답 서보 제어",
    desc: "빠른 응답성이 필요한 산업용 유압 및 모션제어 시스템에 적용됩니다.",
  },
  {
    icon: "ri-settings-3-line",
    title: "모션컨트롤러 연동",
    desc: "Motion Controller와 연계하여 위치·속도·압력 폐루프 제어 시스템을 구성합니다.",
  },
  {
    icon: "ri-ruler-2-line",
    title: "위치 제어",
    desc: "위치센서 피드백을 이용해 유압실린더의 정밀 위치 제어를 구현합니다.",
  },
  {
    icon: "ri-pulse-line",
    title: "압력·하중 제어",
    desc: "압력센서 및 하중 피드백과 연계하여 정밀 압력·하중 제어가 가능합니다.",
  },
];

const APPLICATIONS = [
  "Roll Press 유압 제어",
  "산업용 시험기",
  "정밀 위치 제어 장비",
  "압력·하중 제어 시스템",
  "서보유압 실린더 제어",
  "자동화 및 모션제어 설비",
];

export default function ServoValvePage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Servo Valve Solution"
        title="서보밸브 (Servo Valve / SERVOVALVE)"
        subtitle="MOOG 서보밸브 기반의 정밀 유압·모션제어 솔루션"
        crumbs={[
          { label: "HOME", to: "/" },
          { label: "Servo Valve" },
        ]}
        imageUrl="https://storage.helloreaddy.io/project_files/d69eff6a-141f-4a4c-87b1-85937ec8d7bf/3f140483-30a2-49e4-8e42-65be9cf140c0_compressed___T__.webp"
      />

      {/* Intro */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-4">
            <span className="w-6 h-px bg-accent-500" />
            Servo Valve
            <span className="w-6 h-px bg-accent-500" />
          </span>

          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 mb-6">
            정밀 유압제어를 위한{" "}
            <span className="text-primary-700">서보밸브</span>
          </h2>

          <p className="text-base md:text-lg text-foreground-800 leading-relaxed">
            엠씨테크(MC TECH)는 산업용 유압 및 모션제어 시스템에 적용되는{" "}
            <span className="font-bold text-primary-700">
              MOOG 서보밸브
            </span>
            를 비롯한 Servo Valve 관련 제품 공급과 기술지원을 제공합니다.
            서보밸브(Servo Valve, SERVOVALVE)는 전기 제어신호에 따라 유압의
            유량과 방향을 정밀하게 제어하며, 위치·속도·압력·하중 제어가
            필요한 고응답 유압 시스템에 적용됩니다.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-10 md:mb-14 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              Servo Valve Control
              <span className="w-6 h-px bg-accent-500" />
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
              서보밸브 주요 제어 기능
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((item, i) => (
              <div
                key={item.title}
                className="bg-background-50 border border-background-200 rounded-xl p-6 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i
                      className={`${item.icon} text-2xl w-6 h-6 flex items-center justify-center`}
                    />
                  </div>

                  <span className="text-xs font-heading font-bold text-secondary-500 tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  {item.title}
                </h3>

                <p className="text-sm text-foreground-700 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOOG */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              MOOG Servo Valve
            </span>

            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 leading-tight">
              MOOG 서보밸브 기반{" "}
              <span className="text-primary-700">정밀 제어 시스템</span>
            </h2>

            <p className="mt-5 text-base text-foreground-700 leading-relaxed">
              MC TECH는 MOOG Servo Valve를 활용한 서보유압 시스템의
              구성, 제어 프로그램, 센서 연동 및 현장 시운전을 지원합니다.
              시스템 요구사항에 따라 서보밸브와 실린더, 위치센서,
              압력센서, Motion Controller를 연계하여 정밀 폐루프
              제어 시스템을 구성할 수 있습니다.
            </p>
          </div>

          <div className="bg-background-100 border border-background-200 rounded-xl p-7">
            <h3 className="text-xl font-heading font-bold text-foreground-950 mb-5">
              주요 적용 분야
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {APPLICATIONS.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-4 py-3"
                >
                  <i className="ri-check-line text-primary-600" />
                  <span className="text-sm font-medium text-foreground-900">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Engineering */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950 mb-5">
            서보밸브 공급부터{" "}
            <span className="text-primary-700">현장 시운전까지</span>
          </h2>

          <p className="text-base text-foreground-700 leading-relaxed">
            서보밸브 선정 및 공급뿐만 아니라 유압회로 검토, 센서 구성,
            모션컨트롤러 프로그램, 파라미터 설정, 시스템 튜닝 및 현장
            시운전까지 통합 기술지원을 제공합니다.
          </p>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
}
